import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import AccountSidebar from '../../components/AccountSidebar';
import { 
  FolderIcon,
  PlusIcon,
  TrashIcon,
  ShareIcon,
  EyeIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

// Mock collections data
const mockCollections = [
  {
    id: 1,
    name: 'Wedding Inspiration',
    description: 'Beautiful designs for our upcoming wedding',
    createdDate: '2024-01-15',
    itemCount: 12,
    isPublic: true,
    views: 45,
    followers: 8,
    coverImage: '💒'
  },
  {
    id: 2,
    name: 'Birthday Party Ideas',
    description: 'Fun and colorful designs for birthday celebrations',
    createdDate: '2024-01-12',
    itemCount: 8,
    isPublic: true,
    views: 23,
    followers: 3,
    coverImage: '🎂'
  },
  {
    id: 3,
    name: 'Business Cards',
    description: 'Professional business card designs',
    createdDate: '2024-01-10',
    itemCount: 15,
    isPublic: false,
    views: 0,
    followers: 0,
    coverImage: '💼'
  },
  {
    id: 4,
    name: 'Holiday Cards',
    description: 'Festive designs for all seasons',
    createdDate: '2024-01-08',
    itemCount: 20,
    isPublic: true,
    views: 67,
    followers: 12,
    coverImage: '🎄'
  },
  {
    id: 5,
    name: 'Personal Projects',
    description: 'Private collection for personal use',
    createdDate: '2024-01-05',
    itemCount: 5,
    isPublic: false,
    views: 0,
    followers: 0,
    coverImage: '🔒'
  }
];

const Collections: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [collections, setCollections] = useState(mockCollections);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    isPublic: true
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const handleDeleteCollection = (collectionId: number) => {
    setCollections(collections.filter(collection => collection.id !== collectionId));
  };

  const handleTogglePublic = (collectionId: number) => {
    setCollections(collections.map(collection => 
      collection.id === collectionId 
        ? { ...collection, isPublic: !collection.isPublic }
        : collection
    ));
  };

  const handleCreateCollection = () => {
    if (newCollection.name.trim()) {
      const collection = {
        id: Math.max(...collections.map(c => c.id)) + 1,
        name: newCollection.name,
        description: newCollection.description,
        createdDate: new Date().toISOString().split('T')[0],
        itemCount: 0,
        isPublic: newCollection.isPublic,
        views: 0,
        followers: 0,
        coverImage: '📁'
      };
      setCollections([collection, ...collections]);
      setNewCollection({ name: '', description: '', isPublic: true });
      setShowCreateModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Collections</h1>
          <p className="text-gray-600">Organize your favorite designs into collections</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <AccountSidebar currentPage="collections" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {collections.length} collection{collections.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors"
                    style={{ backgroundColor: '#2e3a47' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Create Collection</span>
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'grid' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'grid' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">⊞</span>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'list' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'list' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">☰</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Collections Grid/List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {collections.map((collection) => (
                  <div key={collection.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                      <span className="text-4xl">{collection.coverImage}</span>
                      <div className="absolute top-2 right-2 flex space-x-1">
                        <button className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50">
                          <ShareIcon className="w-4 h-4 text-gray-600" />
                        </button>
                        <button 
                          onClick={() => handleDeleteCollection(collection.id)}
                          className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-red-50"
                        >
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm line-clamp-1">
                          {collection.name}
                        </h3>
                        <button
                          onClick={() => handleTogglePublic(collection.id)}
                          className={`px-2 py-1 text-xs rounded-full ${
                            collection.isPublic 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {collection.isPublic ? 'Public' : 'Private'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                        {collection.description}
                      </p>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                          <span className="flex items-center">
                            <FolderIcon className="w-3 h-3 mr-1" />
                            {collection.itemCount} items
                          </span>
                          <span className="flex items-center">
                            <EyeIcon className="w-3 h-3 mr-1" />
                            {collection.views} views
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {collection.followers} followers
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mb-3">
                        Created {new Date(collection.createdDate).toLocaleDateString()}
                      </p>
                      <div className="space-y-2">
                        <Link
                          to={`/collections/${collection.id}`}
                          className="w-full text-white py-2 px-3 rounded-lg transition-colors text-center block text-sm"
                          style={{ backgroundColor: '#2e3a47' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                        >
                          View Collection
                        </Link>
                        <button className="w-full border border-gray-300 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                          Edit Collection
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {collections.map((collection) => (
                  <div key={collection.id} className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">{collection.coverImage}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{collection.name}</h3>
                            <p className="text-sm text-gray-500 mb-2">
                              {collection.description}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                              <span className="flex items-center">
                                <FolderIcon className="w-4 h-4 mr-1" />
                                {collection.itemCount} items
                              </span>
                              <span className="flex items-center">
                                <EyeIcon className="w-4 h-4 mr-1" />
                                {collection.views} views
                              </span>
                              <span>{collection.followers} followers</span>
                            </div>
                            <p className="text-xs text-gray-400">
                              Created {new Date(collection.createdDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleTogglePublic(collection.id)}
                              className={`px-3 py-1 text-sm rounded-full ${
                                collection.isPublic 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {collection.isPublic ? 'Public' : 'Private'}
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600">
                              <ShareIcon className="w-4 h-4" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600">
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteCollection(collection.id)}
                              className="p-2 text-red-400 hover:text-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mt-3">
                          <Link
                            to={`/collections/${collection.id}`}
                            className="text-white py-2 px-4 rounded-lg transition-colors text-sm"
                            style={{ backgroundColor: '#2e3a47' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                          >
                            View Collection
                          </Link>
                          <button className="border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                            Edit Collection
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {collections.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="text-6xl mb-4">📁</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No collections yet</h3>
                <p className="text-gray-600 mb-6">
                  Create your first collection to organize your favorite designs
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create Your First Collection
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Create Collection Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Collection</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Collection Name
                  </label>
                  <input
                    type="text"
                    value={newCollection.name}
                    onChange={(e) => setNewCollection({...newCollection, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    placeholder="Enter collection name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newCollection.description}
                    onChange={(e) => setNewCollection({...newCollection, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    rows={3}
                    placeholder="Describe your collection"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={newCollection.isPublic}
                    onChange={(e) => setNewCollection({...newCollection, isPublic: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="isPublic" className="text-sm text-gray-700">
                    Make this collection public
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCollection}
                  className="px-4 py-2 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  Create Collection
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Collections; 